import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle } from "lucide-react";
import { subscribeConfirm } from "../utils/confirm";
import { buttonSecondarySmCls, buttonPrimarySmCls } from "../styles/ui";

// Renders confirmDialog() requests (utils/confirm.js). Mounted once in Layout.
// Portalled to <body> so it never sits inside a host <form> (the nested-form
// trap documented for ChecklistEditorModal) and always stacks above panels.
export default function ConfirmHost() {
  const [req, setReq] = useState(null);
  const cancelRef = useRef(null);
  const confirmRef = useRef(null);

  useEffect(() => subscribeConfirm(setReq), []);

  useEffect(() => {
    if (!req) return;
    // A destructive action starts on Cancel, so a stray Enter can't delete.
    (req.danger ? cancelRef : confirmRef).current?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") { e.stopPropagation(); req.resolve(false); }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [req]);

  if (!req) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-dialog flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) req.resolve(false); }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        aria-describedby={req.message ? "confirm-message" : undefined}
        className="w-full max-w-md bg-surface rounded-xl border border-line shadow-xl p-5"
      >
        <div className="flex items-start gap-3">
          {req.danger && (
            <span className="shrink-0 rounded-full bg-danger-50 p-2">
              <AlertTriangle className="w-5 h-5 text-danger-600" aria-hidden="true" />
            </span>
          )}
          <div className="min-w-0">
            <h2 id="confirm-title" className="text-base font-semibold text-ink">{req.title}</h2>
            {req.message && (
              <p id="confirm-message" className="mt-1.5 text-sm text-ink-muted whitespace-pre-line">{req.message}</p>
            )}
          </div>
        </div>
        <div className="mt-5 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <button
            ref={cancelRef}
            type="button"
            onClick={() => req.resolve(false)}
            className={`${buttonSecondarySmCls} justify-center min-h-11 sm:min-h-0 sm:py-2 sm:px-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500`}
          >
            {req.cancelLabel}
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={() => req.resolve(true)}
            className={
              req.danger
                ? "flex items-center justify-center text-sm font-semibold text-white bg-danger-600 hover:bg-danger-700 rounded-lg px-4 py-2 min-h-11 sm:min-h-0 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger-500 focus-visible:ring-offset-2"
                : `${buttonPrimarySmCls} justify-center min-h-11 sm:min-h-0 sm:py-2 sm:px-4`
            }
          >
            {req.confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
